import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import { LockOutlined, InfoOutlined, ContentCopyOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionCycleService } from '../../../services/admissions/admissionCycleService';
import { classesApi } from '../../../services/sessions/classesApi';
import { AdmissionCycle, AssessmentPathway, CycleEditLocks } from '../../../types/admissions';
import { useModulePermissions } from '../../../hooks/useModulePermissions';

import CycleDatesSection from './CycleDatesSection';
import ApplicationFeeSection from './ApplicationFeeSection';
import SeatMatrixSection from './SeatMatrixSection';
import EligibilitySection from './EligibilitySection';
import AdmissionFeesSection from './AdmissionFeesSection';
import RefundPolicySection from './RefundPolicySection';
import OfferTermsSection from './OfferTermsSection';
import OfferDeadlineSection from './OfferDeadlineSection';
import ApplicationFormSection from './ApplicationFormSection';
import ScoringConfigSection from './ScoringConfigSection';
import StagesSection from './StagesSection';
import TestConfigSection from './TestConfigSection';
import TestScheduleSection from './TestScheduleSection';
import InterviewConfigSection from './InterviewConfigSection';
import DocumentsSection from './DocumentsSection';
import AdmitCardSection from './AdmitCardSection';
import ProspectusSection from './ProspectusSection';
import CopyFromCycleDialog from './CopyFromCycleDialog';
import PathwaySwitcher from './PathwaySwitcher';

/**
 * CycleSettingsTab — vertical stack of editable settings sections. Each
 * section owns its local edit state; saving a section runs through
 * `onSaveCycle(patch)` here so a single codepath updates the cycle and
 * refreshes the shared data for sibling sections.
 *
 * Status rules enforced by the backend's `updateCycle`:
 *   • Draft  — anything editable.
 *   • Open   — editable, except what applicants' records already depend on
 *              (marks, offers, answers, issued admit cards). The backend's
 *              edit locks name those up front, section by section; anything
 *              finer (seats below the offers made…) is explained at save.
 *   • Closed — read-only, except what the remaining tests and admit cards
 *              need: test dates, admit-card instructions, the prospectus.
 *   • Archived — read-only.
 * "Copy from another cycle" replaces whole sections, so it stays Draft-only.
 */

interface SchoolClass { _id: string; name: string }

interface Props {
  cycle: AdmissionCycle;
  onChanged: (next: AdmissionCycle) => void;
}

const CycleSettingsTab: React.FC<Props> = ({ cycle, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [activeGroup, setActiveGroup] = useState('dates');

  // Highlight the group currently in view in the side navigation.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-settings-group]'));
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries.filter(e => e.isIntersecting).sort((x, y) => x.boundingClientRect.top - y.boundingClientRect.top)[0];
        if (top) setActiveGroup(top.target.getAttribute('data-settings-group') || 'dates');
      },
      { rootMargin: '-90px 0px -55% 0px' },
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingClasses(true);
      try {
        const list: SchoolClass[] = await classesApi.list();
        if (!cancelled) setClasses(Array.isArray(list) ? list : []);
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Failed to load classes', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  // Editing needs the admissions_settings permission (the backend's gate) and
  // a cycle that isn't closed. On an open cycle, what applicants' records
  // depend on comes back as edit locks, so those sections say why up front
  // instead of failing at save.
  const { canWrite } = useModulePermissions();
  const canConfigure = canWrite('admissions_settings');
  const isOpen = cycle.status === 'Open';
  const isDraft = cycle.status === 'Draft';
  const isFrozen = cycle.status === 'Closed' || cycle.status === 'Archived';
  const canEdit = canConfigure && !isFrozen;
  // A closed cycle still runs its tests and admit cards.
  const canEditRunning = canConfigure && cycle.status !== 'Archived';
  const [copyOpen, setCopyOpen] = useState(false);

  const [locks, setLocks] = useState<CycleEditLocks | null>(null);
  useEffect(() => {
    if (!isOpen) { setLocks(null); return undefined; }
    let cancelled = false;
    admissionCycleService.editLocks(cycle._id)
      .then((l) => { if (!cancelled) setLocks(l); })
      // Saves are still checked on the server, so editing stays safe.
      .catch(() => { if (!cancelled) enqueueSnackbar('Couldn\'t check which settings are fixed. You can still edit — every save is checked.', { variant: 'warning' }); });
    return () => { cancelled = true; };
  }, [cycle._id, cycle.updatedAt, isOpen, enqueueSnackbar]);

  const saveSection = useCallback(async (patch: Partial<AdmissionCycle>): Promise<AdmissionCycle> => {
    try {
      const next = await admissionCycleService.update(cycle._id, patch);
      enqueueSnackbar('Saved', { variant: 'success' });
      onChanged(next);
      return next;
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to save', { variant: 'error' });
      throw err;
    }
  }, [cycle._id, enqueueSnackbar, onChanged]);
  const saveCycle = async (patch: Partial<AdmissionCycle>): Promise<void> => { await saveSection(patch); };

  // The pathway the Selection process and Merit sections edit.
  const pathways = cycle.pathways || [];
  const [pathwayKey, setPathwayKey] = useState(pathways[0]?.key);
  const pathway = pathways.find(p => p.key === pathwayKey) ?? pathways[0];
  const multiple = pathways.length > 1;
  const pathwayLocks = locks?.pathways[pathway.key];
  const savePathway = async (patch: Partial<AssessmentPathway>): Promise<void> => {
    await saveSection({ pathways: pathways.map(p => (p.key === pathway.key ? { ...p, ...patch } : p)) });
  };
  const savePathways = useCallback(async (next: AssessmentPathway[], select: string): Promise<void> => {
    const saved = await saveSection({ pathways: next });
    setPathwayKey(saved.pathways.find(p => p.name === select)?.key ?? saved.pathways[0]?.key);
  }, [saveSection]);

  // Scope the class list to the cycle's allowlist (if set). Keeps the
  // eligibility / fee / pathway pickers from offering classes that will
  // never apply.
  const allowedClassSet = new Set(
    (cycle.allowedClassIds || []).map(c => typeof c === 'object' ? c._id : c),
  );
  const classesForCycle = allowedClassSet.size > 0
    ? classes.filter(c => allowedClassSet.has(c._id))
    : classes;

  const groups: Array<{ key: string; title: string; hint: string; body: React.ReactNode }> = [
    {
      key: 'dates', title: 'Dates', hint: 'When applications open and close',
      body: <CycleDatesSection cycle={cycle} canEdit={canEdit} onSave={saveCycle} />,
    },
    {
      key: 'seats', title: 'Seats & eligibility', hint: 'Who can apply and how many seats each class has',
      body: (
        <>
          {/* Every school class — the seat matrix is where a class joins the cycle. */}
          <SeatMatrixSection cycle={cycle} classes={classes} loadingClasses={loadingClasses} canEdit={canEdit} onSave={saveCycle} />
          <EligibilitySection cycle={cycle} classes={classesForCycle} loadingClasses={loadingClasses} canEdit={canEdit} onSave={saveCycle} />
        </>
      ),
    },
    {
      key: 'form', title: 'Application form', hint: 'What parents fill in',
      body: <ApplicationFormSection cycle={cycle} canEdit={canEdit} answeredKeys={locks?.answeredFieldKeys} onChanged={onChanged} />,
    },
    {
      key: 'process',
      title: 'Selection process',
      hint: multiple
        ? `How each group of classes is assessed. Showing ${pathway.name}.`
        : 'Stages, documents, admit cards, tests and interviews',
      body: (
        <>
          {(canEdit || multiple) && (
            <PathwaySwitcher
              cycle={cycle}
              classes={classesForCycle}
              selected={pathway}
              onSelect={setPathwayKey}
              canEdit={canEdit}
              onSave={savePathways}
            />
          )}
          {/* Keyed by pathway so an edit in progress never carries across. */}
          <StagesSection key={`stages-${pathway.key}`} pathway={pathway} canEdit={canEdit} onSave={savePathway} />
          <DocumentsSection key={`docs-${pathway.key}`} pathway={pathway} canEdit={canEdit} onSave={savePathway} />
          <TestConfigSection key={`test-${pathway.key}`} pathway={pathway} canEdit={canEdit} lockedReason={pathwayLocks?.test} onSave={savePathway} />
          <InterviewConfigSection key={`interview-${pathway.key}`} pathway={pathway} canEdit={canEdit} lockedReason={pathwayLocks?.interview} onSave={savePathway} />
          {/* Cycle-wide: one numbering for every card. */}
          <AdmitCardSection
            cycle={cycle}
            canEdit={canEditRunning}
            numberingLockedReason={cycle.status === 'Closed' ? 'The cycle is closed — reopen it to change the numbering.' : locks?.admitCardNumbering}
            onSave={saveCycle}
          />
          {/* Dates per class, so it lists this pathway's classes. Tests get rescheduled. */}
          <TestScheduleSection key={`schedule-${pathway.key}`} cycle={cycle} pathway={pathway} classes={classesForCycle} canEdit={canEditRunning} onSave={saveCycle} />
        </>
      ),
    },
    {
      key: 'merit',
      title: 'Merit & ranking',
      hint: multiple ? `How ${pathway.name} applicants are scored and ranked` : 'How applicants are scored and ranked',
      body: <ScoringConfigSection key={`merit-${pathway.key}`} pathway={pathway} canEdit={canEdit} lockedReason={pathwayLocks?.merit ?? pathwayLocks?.custom} onSave={savePathway} />,
    },
    {
      key: 'fees', title: 'Fees & offer', hint: 'Application and admission fees, refunds, offer deadline and terms',
      body: (
        <>
          <ApplicationFeeSection cycle={cycle} canEdit={canEdit} onSave={saveCycle} />
          <AdmissionFeesSection cycle={cycle} classes={classesForCycle} loadingClasses={loadingClasses} canEdit={canEdit} lockedClassIds={locks?.feeLockedClassIds} onSave={saveCycle} />
          <RefundPolicySection cycle={cycle} canEdit={canEdit} onSave={saveCycle} />
          <OfferDeadlineSection cycle={cycle} canEdit={canEdit} onSave={saveCycle} />
          <OfferTermsSection cycle={cycle} canEdit={canEdit} onSave={saveCycle} />
        </>
      ),
    },
    {
      key: 'materials', title: 'Materials', hint: 'The prospectus parents can download',
      body: <ProspectusSection cycle={cycle} canEdit={canEditRunning} onChanged={onChanged} />,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {canEdit && isDraft && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          sx={{ mb: 3, p: 2, borderRadius: 2.5, border: 1, borderColor: 'divider', bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>Setting this cycle up?</Typography>
            <Typography variant="body2" color="text.secondary">
              Copy seats, the form, the selection process, merit and fees from another cycle, then adjust what&apos;s different.
            </Typography>
          </Box>
          <Button variant="outlined" size="small" startIcon={<ContentCopyOutlined fontSize="small" />} onClick={() => setCopyOpen(true)} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Copy from another cycle
          </Button>
        </Stack>
      )}
      {copyOpen && (
        <CopyFromCycleDialog
          open
          cycle={cycle}
          onClose={() => setCopyOpen(false)}
          onCopied={(next) => { setCopyOpen(false); onChanged(next); }}
        />
      )}

      {(isFrozen || isOpen) && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          sx={{ mb: 3, p: 2, borderRadius: 2.5, bgcolor: (t) => alpha(t.palette.info.main, 0.06), border: 1, borderColor: (t) => alpha(t.palette.info.main, 0.2) }}
        >
          {isFrozen
            ? <LockOutlined sx={{ fontSize: 18, color: 'info.main', mt: 0.25 }} />
            : <InfoOutlined sx={{ fontSize: 18, color: 'info.main', mt: 0.25 }} />}
          <Typography variant="body2" color="text.secondary">
            {isFrozen ? (
              cycle.status === 'Closed'
                ? <>This cycle is <b>closed</b>. You can still change test dates, admit-card instructions and the prospectus — reopen the cycle to change anything else.</>
                : <>This cycle is <b>archived</b>, so its settings are read-only.</>
            ) : (
              <>
                This cycle is <b>open</b>, and its settings can still change — new rules apply to applicants from now on.
                Anything applicants&apos; records already depend on, like marks, offers and answers, stays as it is; those
                sections say why.
              </>
            )}
          </Typography>
        </Stack>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '220px 1fr' }, gap: 4, alignItems: 'start' }}>
        {/* Side navigation */}
        <Box sx={{ position: 'sticky', top: 88, display: { xs: 'none', lg: 'block' } }}>
          <Stack spacing={0.25}>
            {groups.map(g => {
              const active = activeGroup === g.key;
              return (
                <Box
                  key={g.key}
                  onClick={() => document.getElementById(`settings-${g.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  sx={{
                    px: 1.5, py: 1, borderRadius: 2, cursor: 'pointer',
                    bgcolor: (t) => (active ? alpha(t.palette.primary.main, 0.08) : 'transparent'),
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, active ? 0.1 : 0.04) },
                    transition: 'background-color 120ms',
                  }}
                >
                  <Typography variant="body2" fontWeight={active ? 600 : 500} color={active ? 'primary.main' : 'text.primary'}>
                    {g.title}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Groups */}
        <Stack spacing={5} sx={{ minWidth: 0 }}>
          {groups.map(g => (
            <Box key={g.key} id={`settings-${g.key}`} data-settings-group={g.key} sx={{ scrollMarginTop: 88 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>{g.title}</Typography>
                <Typography variant="body2" color="text.secondary">{g.hint}</Typography>
              </Box>
              <Stack spacing={2}>{g.body}</Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default CycleSettingsTab;
